import type { ComponentType, CSSProperties } from "react";
import { AtSign, Bookmark, Clock, GitBranch, Inbox } from "lucide-react";
import { ICON_STROKE } from "~/lib/icon";
import { IconTooltip } from "~/components/IconTooltip";

export interface ActivityBarProps {
  activityCount: number;
  threadsHasUnread: boolean;
  scheduledCount: number;
  activeView: string;
  onOpenUnreads: () => void;
  onOpenActivity: () => void;
  onOpenThreads: () => void;
  onOpenSaved: () => void;
  onOpenScheduled: () => void;
}

export function ActivityBar({
  activityCount,
  threadsHasUnread,
  scheduledCount,
  activeView,
  onOpenUnreads,
  onOpenActivity,
  onOpenThreads,
  onOpenSaved,
  onOpenScheduled,
}: ActivityBarProps) {
  return (
    <nav
      aria-label="Activity"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "var(--chat-space-2) var(--chat-space-2) 0",
        flexShrink: 0,
      }}
    >
      <ActivityButton
        icon={Inbox}
        label="Unreads"
        active={activeView === "unreads"}
        onActivate={onOpenUnreads}
      />
      <ActivityButton
        icon={AtSign}
        label="Activity"
        count={activityCount}
        tone="mention"
        active={activeView === "mentions"}
        onActivate={onOpenActivity}
      />
      <ActivityButton
        icon={GitBranch}
        label="Threads"
        dot={threadsHasUnread}
        active={activeView === "threads"}
        onActivate={onOpenThreads}
      />
      <ActivityButton
        icon={Bookmark}
        label="Saved"
        active={activeView === "saved"}
        onActivate={onOpenSaved}
      />
      {(scheduledCount > 0 || activeView === "scheduled") && (
        <ActivityButton
          icon={Clock}
          label="Scheduled"
          count={scheduledCount}
          active={activeView === "scheduled"}
          onActivate={onOpenScheduled}
        />
      )}
    </nav>
  );
}

function ActivityButton({
  icon: Icon,
  label,
  active,
  onActivate,
  count,
  dot = false,
  tone = "default",
}: {
  icon: ComponentType<{ size?: number; strokeWidth?: number; style?: CSSProperties }>;
  label: string;
  active: boolean;
  onActivate: () => void;
  count?: number;
  dot?: boolean;
  tone?: "default" | "mention";
}) {
  const visibleCount = typeof count === "number" ? count : 0;
  const showCount = visibleCount > 0;
  const accessibleName = showCount ? `${label}, ${visibleCount}` : label;
  return (
    <IconTooltip label={label}>
      <button
        type="button"
        onClick={onActivate}
        aria-pressed={active}
        aria-label={accessibleName}
        className="chat-button"
        style={{
          flex: 1,
          minWidth: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "var(--chat-space-1)",
          height: "var(--chat-row-comfortable-channel)",
          padding: "0 var(--chat-space-1)",
          border: "none",
          borderRadius: "var(--chat-radius-sm)",
          background: active ? "var(--chat-surface-raised)" : "transparent",
          color: active ? "var(--chat-fg-primary)" : "var(--chat-fg-muted)",
          cursor: "pointer",
        }}
      >
        <Icon size={14} strokeWidth={ICON_STROKE} style={{ flexShrink: 0 }} />
        {showCount && (
          <span
            aria-hidden
            style={{
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-xs-size)",
              lineHeight: "var(--chat-type-body-xs-line)",
              fontVariantNumeric: "tabular-nums",
              color: tone === "mention" ? "var(--chat-accent)" : "inherit",
            }}
          >
            {visibleCount > 99 ? "99+" : visibleCount}
          </span>
        )}
        {dot && (
          <span
            className="chat-activity-dot"
            aria-hidden
            style={{
              width: 6,
              height: 6,
              flexShrink: 0,
              borderRadius: "var(--chat-radius-full)",
              background: "var(--chat-accent)",
            }}
          />
        )}
      </button>
    </IconTooltip>
  );
}
